import { useState, useRef, useEffect, useCallback, useMemo } from 'react';

// Strip diacritics for accent-insensitive search (é→e, ô→o, etc.)
const normalize = (str) =>
  str.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();

export default function SearchSelect({
  options = [],
  value,
  onChange,
  placeholder = '-- Sélectionner --',
  name,
  required,
  className = '',
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [highlighted, setHighlighted] = useState(-1);
  const [dropUp, setDropUp] = useState(false);
  const containerRef = useRef(null);
  const inputRef = useRef(null);
  const listRef = useRef(null);

  const selected = options.find((o) => String(o.value) === String(value));

  // Multi-word, accent-insensitive filtering
  const filtered = useMemo(() => {
    if (!search.trim()) return options;
    const terms = normalize(search).split(/\s+/).filter(Boolean);
    return options.filter((o) => {
      const label = normalize(o.label);
      return terms.every((t) => label.includes(t));
    });
  }, [options, search]);

  const handleSelect = useCallback((val) => {
    onChange({ target: { name, value: val } });
    setOpen(false);
    setSearch('');
    setHighlighted(-1);
  }, [name, onChange]);

  const handleKeyDown = (e) => {
    if (!open) {
      if (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowDown') {
        e.preventDefault();
        setOpen(true);
      }
      return;
    }
    if (e.key === 'Escape') { setOpen(false); setSearch(''); setHighlighted(-1); return; }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlighted((h) => (filtered.length === 0 ? -1 : Math.min(h + 1, filtered.length - 1)));
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlighted((h) => (filtered.length === 0 ? -1 : Math.max(h - 1, 0)));
    }
    if (e.key === 'Enter' && highlighted >= 0 && highlighted < filtered.length) {
      e.preventDefault();
      handleSelect(filtered[highlighted].value);
    }
  };

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handle = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
        setSearch('');
        setHighlighted(-1);
      }
    };
    document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
  }, [open]);

  // Auto-focus search input & decide dropdown direction
  useEffect(() => {
    if (open && inputRef.current) inputRef.current.focus();
    if (open && containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      setDropUp(spaceBelow < 300 && rect.top > spaceBelow);
    }
  }, [open]);

  // Scroll highlighted item into view
  useEffect(() => {
    if (highlighted >= 0 && listRef.current) {
      const offset = !required ? 1 : 0; // account for placeholder <li>
      const el = listRef.current.children[highlighted + offset];
      if (el) el.scrollIntoView({ block: 'nearest' });
    }
  }, [highlighted, required]);

  return (
    <div className={`search-select ${className}`} ref={containerRef}>
      <button
        type="button"
        className={`search-select-trigger ${open ? 'active' : ''} ${!selected ? 'placeholder' : ''}`}
        onClick={() => setOpen(!open)}
        onKeyDown={handleKeyDown}
      >
        <span className="search-select-value">
          {selected ? selected.label : placeholder}
        </span>
        <i className={`bi bi-chevron-${open ? 'up' : 'down'}`} />
      </button>

      {required && (
        <input
          tabIndex={-1}
          className="search-select-hidden"
          value={value || ''}
          required={required}
          onChange={() => {}}
        />
      )}

      {open && (
        <div className={`search-select-dropdown ${dropUp ? 'drop-up' : ''}`}>
          <div className="search-select-search">
            <i className="bi bi-search" />
            <input
              ref={inputRef}
              type="text"
              placeholder="Rechercher…"
              value={search}
              onChange={(e) => { setSearch(e.target.value); setHighlighted(0); }}
              onKeyDown={handleKeyDown}
            />
            {search && (
              <button
                type="button"
                className="search-select-clear-search"
                onClick={() => { setSearch(''); setHighlighted(-1); inputRef.current?.focus(); }}
              >
                <i className="bi bi-x" />
              </button>
            )}
          </div>
          <ul className="search-select-list" ref={listRef}>
            {!required && (
              <li
                className={`search-select-option ${!value ? 'selected' : ''}`}
                onClick={() => handleSelect('')}
              >
                {placeholder}
              </li>
            )}
            {filtered.length === 0 ? (
              <li className="search-select-empty">Aucun résultat</li>
            ) : (
              filtered.map((o, idx) => (
                <li
                  key={o.value}
                  className={`search-select-option ${String(o.value) === String(value) ? 'selected' : ''} ${idx === highlighted ? 'highlighted' : ''}`}
                  onClick={() => handleSelect(o.value)}
                  onMouseEnter={() => setHighlighted(idx)}
                >
                  {o.label}
                </li>
              ))
            )}
          </ul>
          {filtered.length > 0 && search && (
            <div className="search-select-count">
              {filtered.length} résultat{filtered.length > 1 ? 's' : ''}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
